import React, { useState } from 'react';
import { Sparkles, ArrowRight, CheckCircle } from 'lucide-react';
import { categorizeTransactions } from '../utils/openai';

export const AIDemo: React.FC = () => {
  const [demoTransactions] = useState([
    '$75 at Save-On-Foods',
    '$1300 from ACME Rent',
    '$27 at Shell Gas Station',
    '$45 at Starbucks',
    '$1200 from Salary Payment',
    '$89 at Amazon.com',
    '$15 for Netflix Subscription',
    '$65 at CVS Pharmacy'
  ]);

  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDemo = async () => {
    setLoading(true);
    setError('');
    setResults([]);

    try {
      const result = await categorizeTransactions(demoTransactions);
      setResults(result.categorizations);
    } catch (err) {
      setError('Failed to categorize transactions. Please check your OpenAI API key.');
      console.error('Demo error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Food & Dining': 'bg-green-100 text-green-800',
      'Transportation': 'bg-blue-100 text-blue-800',
      'Housing': 'bg-purple-100 text-purple-800',
      'Income': 'bg-emerald-100 text-emerald-800',
      'Shopping': 'bg-orange-100 text-orange-800',
      'Entertainment': 'bg-pink-100 text-pink-800',
      'Healthcare': 'bg-red-100 text-red-800',
      'Travel': 'bg-indigo-100 text-indigo-800',
      'Education': 'bg-yellow-100 text-yellow-800',
      'Business': 'bg-gray-100 text-gray-800',
      'Utilities': 'bg-cyan-100 text-cyan-800',
      'Insurance': 'bg-lime-100 text-lime-800',
      'Other': 'bg-gray-100 text-gray-800',
    };
    return colors[category] || colors['Other'];
  };

  return (
    <div className="card dark:border-gray-700 dark:shadow-lg">
      <div className="flex items-center space-x-2 mb-4">
        <Sparkles className="w-5 h-5 text-primary-600" />
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">AI Transaction Categorization Demo</h2>
      </div>
      
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        See how AI automatically categorizes your transactions. This demo uses the exact format you provided.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Transactions */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Sample Transactions</h3>
          <div className="space-y-2">
            {demoTransactions.map((transaction, index) => (
              <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm dark:text-gray-100">
                {transaction}
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">AI Categorization Results</h3>
            <button
              onClick={handleDemo}
              disabled={loading}
              className="btn-primary text-sm py-1 px-3 flex items-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
              ) : (
                <Sparkles className="w-3 h-3 mr-2" />
              )}
              {loading ? 'Processing...' : 'Run Demo'}
            </button>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/30 p-3 rounded-lg mb-3">
              {error}
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-2">
              {results.map((result, index) => (
                <div key={index} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {result.transaction}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(result.category)}`}> 
                        {result.category}
                      </span>
                      <div className="flex items-center space-x-1">
                        <CheckCircle className="w-3 h-3 text-success-600" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {(result.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && results.length === 0 && !error && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-300">
              <ArrowRight className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-400" />
              <p className="text-sm">Click "Run Demo" to see AI categorization in action</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 p-4 bg-primary-50 dark:bg-gray-900 rounded-lg border dark:border-gray-700">
        <h4 className="text-sm font-medium text-primary-900 dark:text-primary-200 mb-2">How it works:</h4>
        <ul className="text-xs text-primary-800 dark:text-primary-300 space-y-1">
          <li>• AI analyzes transaction descriptions to understand the context</li>
          <li>• Automatically categorizes into predefined categories</li>
          <li>• Provides confidence scores for each categorization</li>
          <li>• Works with natural language descriptions</li>
        </ul>
      </div>
    </div>
  );
}; 